import { useState, useEffect } from 'react';
import React from 'react';
import { chatApi, type ChatStatus } from '@/services/api/agentApi';
import { ensureOptionalAgentServiceAvailable } from '@/services/api/agentServiceAvailability';

export interface UseAgentStatusReturn {
  agentStatus: { available: ChatStatus['available']; orchestrators: string[] };
  setAgentStatus: React.Dispatch<React.SetStateAction<{ available: ChatStatus['available']; orchestrators: string[] }>>;
}

export function useAgentStatus(): UseAgentStatusReturn {
  const [agentStatus, setAgentStatus] = useState<{ available: ChatStatus['available']; orchestrators: string[] }>({
    available: false,
    orchestrators: [],
  });

  // Phase 2: Check agent status on mount
  useEffect(() => {
    let mounted = true;

    async function fetchAgentStatus(): Promise<void> {
      if (!(await ensureOptionalAgentServiceAvailable())) {
        if (mounted) {
          setAgentStatus({ available: false, orchestrators: [] });
        }
        return;
      }

      chatApi.getStatus()
        .then(res => {
          if (mounted && res.data) {
            setAgentStatus({
              available: res.data.available,
              orchestrators: res.data.orchestrators.map(o => o.name),
            });
          }
        })
        .catch(() => {
          if (mounted) {
            setAgentStatus({ available: false, orchestrators: [] });
          }
        });
    }

    void fetchAgentStatus();
    return () => { mounted = false; };
  }, []);

  // Poll continuously: 15s while offline, 60s while online.
  // This detects both agent startup and post-restart recovery.
  useEffect(() => {
    const interval = setInterval(() => {
      void (async () => {
        if (!(await ensureOptionalAgentServiceAvailable())) {
          setAgentStatus({
            available: false,
            orchestrators: [],
          });
          return;
        }

        chatApi.getStatus()
          .then(res => {
            if (res.data) {
              setAgentStatus({
                available: res.data.available,
                orchestrators: res.data.orchestrators.map(o => o.name),
              });
            }
          })
          .catch(() => setAgentStatus({ available: false, orchestrators: [] }));
      })();
    }, agentStatus.available ? 60000 : 15000);
    return () => clearInterval(interval);
  }, [agentStatus.available]);

  return { agentStatus, setAgentStatus };
}
